import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, unref as _unref, toDisplayString as _toDisplayString, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "rd-mt-2.5 rd-mb-2.5 wrapper loading-skeleton"
}
const _hoisted_2 = { class: "inner-wrapper" }
const _hoisted_3 = { class: "content-container" }
const _hoisted_4 = { class: "content" }
const _hoisted_5 = {
  role: "status",
  class: "motion-safe:rd-pulse-slow rd-space-y-3"
}
const _hoisted_6 = {
  key: 0,
  class: "tabs-loading"
}
const _hoisted_7 = { id: "stripe-element-errors" }
const _hoisted_8 = ["id"]

import { useRenderStore } from "@/builder/stores/renderStore";
import {
    Appearance,
    StripeElement,
    StripeElementsOptionsMode,
    StripePaymentElementOptions,
} from "@stripe/stripe-js";
import {
    PropType,
    computed,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import { generateUID, getPaymentMethodType } from "../../BuilderUtilities";
import { PartialDisplayType, PaymentMethod } from "../../enums";
import { PageDesign } from "../page/PageInterface";
import { IPaymentComponent } from "./PaymentInterface";

// injects

export default /*@__PURE__*/_defineComponent({
  __name: 'StripePaymentComponent',
  props: {
    component: {
        type: Object as PropType<IPaymentComponent>,
        required: true,
    },
    organizationId: {
        type: Number as PropType<number | null>,
        required: true,
        default: null,
    },
    merchantAccount: {
        type: String,
        required: true,
    },
    tokenizationKey: {
        type: String,
        required: true,
    },
    readOnly: {
        type: Boolean,
        required: true,
        default: false,
    },
    pageDesign: {
        type: Object as PropType<PageDesign>,
        required: true,
    },
    formIsOnParentStep: Boolean,
},
  setup(__props) {

const renderStore = useRenderStore();

// props
const props = __props;

// refs
const stripeLoaded = ref(false);
const stripeReady = ref(false);

const stripePaymentElementId = ref(props.component.id + generateUID('_stripe-element'));

const primaryColor = computed(() => {
    return props.pageDesign.primaryColor;
});

const secondaryColor = computed(() => {
    return props.pageDesign.secondaryColor;
});

const fontFamily = computed(() => {
    return props.pageDesign.fontFamily;
});

const fieldStyle = computed(() => {
    return props.pageDesign.fieldStyle;
});

const fieldBorderColor = computed(() => {
    return props.pageDesign.fieldBorderColor;
});

const fieldLabelColor = computed(() => {
    return props.pageDesign.fieldLabelColor;
});

const isModalForm = computed(() => {
    return renderStore.page.partialDisplayType === PartialDisplayType.Modal;
});

const borderRadius = computed(() => {
    //if borderRadius is < 0 we need to return 0 for PayPal button
    const borderRadius = parseInt(props.pageDesign.borderRadius ?? "0");

    if (borderRadius < 0) {
        return 0;
    }

    return borderRadius;
});

const appearance = computed(() => {
    return {
        theme: "stripe",
        variables: {
            colorPrimary: primaryColor.value ?? "#0056B3",
            colorBackground: "#FFFFFF",
            colorText: "#4D5B65",
            colorTextSecondary: secondaryColor.value ?? "#A9B0B4",
            colorTextPlaceholder: "#4D5B65",
            colorDanger: "#B73F1E",
            fontSizeBase: "16px",
            fontFamily: fontFamily.value ?? "Inter, sans-serif",
            spacingUnit: isModalForm.value ? ".25rem" : ".375rem",
            borderRadius:
                fieldStyle.value == 0 ? borderRadius.value + "px" : ".25rem",
            colorIconCardError: "#B73F1E",
            colorIconCardCvcError: "#B73F1E",
        },
        rules: {
            ".Tab": {
                borderColor: fieldBorderColor.value ?? "#DCDBD9",
                boxShadow:
                    "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(18, 42, 66, 0.02)",
                color: "#4D5B65",
                padding: isModalForm.value
                    ? ".5rem .875rem"
                    : ".625rem .9375rem",
            },
            ".TabIcon": {
                fill: "#4D5B65",
            },
            ".TabIcon--selected": {
                fill: primaryColor.value ?? "#0056B3",
            },
            ".Input": {
                borderColor: fieldBorderColor.value ?? "#DCDBD9",
                color: "#4D5B65",
                fontSize: ".875rem",
                lineHeight: isModalForm.value ? "1.625rem" : "2rem",
                padding: ".375rem .75rem",
            },
            ".Input:focus": {
                outline: "none",
                borderColor: fieldBorderColor.value ?? "#DCDBD9",
                boxShadow: "0 0 0.375rem rgba(0, 0, 0, .4)",
            },
            ".Input--invalid": {
                boxShadow: "none",
            },
            ".Label": {
                color: fieldLabelColor.value ?? "#4D5B65",
                fontSize: ".75rem",
                fontWeight: "600",
                marginBottom: ".25rem",
            },
            ".Label--invalid": {
                color: "#B73F1E",
            },
            ".Error": {
                fontSize: ".75rem",
                fontWeight: "400",
            },
            ".PickerItem, .PickerItem--selected": {
                color: "#4D5B65",
                borderColor: fieldBorderColor.value ?? "#DCDBD9",
                fontSize: ".875rem",
                fontWeight: "600",
                padding: isModalForm.value ? ".375rem" : ".625rem 1.25rem",
            },
            ".PickerItem:hover": {
                backgroundColor: "#FAFAFA",
                borderColor: fieldBorderColor.value ?? "#DCDBD9",
            },
            ".PickerItem--selected:hover": {
                borderColor: fieldBorderColor.value ?? "#DCDBD9",
                boxShadow: "none",
            },
            ".AccordionItem": {
                borderColor: fieldBorderColor.value ?? "#DCDBD9",
                padding: isModalForm.value ? "1rem" : "1.5rem",
            },
            ".MenuIcon": {
                fill: primaryColor.value ?? "#0056B3",
            },
            ".MenuIcon--open, .MenuIcon:hover": {
                backgroundColor: "#FFFFFF",
            },
            ".MenuAction, .MenuAction:active, .MenuAction:focus, .MenuAction:hover":
            {
                border: "1px solid #DCDBD9",
                backgroundColor: "#FFFFFF",
                fontSize: ".75rem",
                lineHeight: "1.2",
            },
        },
    } as Appearance;
});

// methods
const destroyElement = (element: StripeElement) => {
    if (element) {
        try {
            element.unmount();
            element.destroy();
        } catch {
            //do nothing
        }
    }
};

const loadStripeElements = () => {
    if (renderStore.stripe && stripeLoaded.value) {
        //element options
        const elementsOptions = {
            mode: "setup",
            locale: props.component.customData.locale ?? "auto",
            currency: renderStore.page.gatewayCurrency?.toLowerCase() ?? 'usd', // stripe currency is always lowercase
            paymentMethodTypes: props.component.customData.paymentMethods,
            captureMethod: "automatic",
            paymentMethodCreation: "manual",
            appearance: appearance.value,
        } as StripeElementsOptionsMode;

        //if bank account is enabled, add bank account options
        if (
            props.component.customData.paymentMethods.some((x) =>
                x.includes(PaymentMethod.US_Bank_Account)
            )
        ) {
            const startDate = new Date(
                renderStore.gift.startDate || new Date()
            );
            const today = new Date();
            today.setHours(
                startDate.getHours(),
                startDate.getMinutes(),
                startDate.getSeconds(),
                startDate.getMilliseconds()
            );
            const isToday = startDate.getTime() === today.getTime();

            // @ts-ignore
            elementsOptions.paymentMethodOptions = {
                us_bank_account: {
                    // @ts-ignore
                    verification_method: isToday
                        ? "instant_or_skip"
                        : "instant",
                    financial_connections: {
                        permissions: ["payment_method"],
                    },
                },
            };
        }

        const billingDetails = renderStore.buildStripeBillingDetails();
        const paymentElementOptions = {
            readOnly: props.readOnly,
            layout: props.component.customData.layout,
            wallets: {
                applePay: props.component.customData.enableWallets
                    ? "auto"
                    : "never",
                googlePay: props.component.customData.enableWallets
                    ? "auto"
                    : "never",
            },
            defaultValues: {
                billingDetails: billingDetails,
            },
            fields: {
                billingDetails: {
                    name: "never",
                    email: "never",
                    address: {
                        postalCode: "auto",
                    },
                },
            },
            terms: {
                applePay: "never",
                auBecsDebit: "never",
                bancontact: "never",
                card: "never",
                cashapp: "never",
                googlePay: "never",
                ideal: "never",
                paypal: "never",
                sepaDebit: "never",
                sofort: "never",
                usBankAccount: "never",
            },
        } as StripePaymentElementOptions;

        renderStore.stripeElements =
            renderStore.stripe.elements(elementsOptions);

        updateStripeElementAmount();

        renderStore.paymentElement = renderStore.stripeElements.create(
            "payment",
            paymentElementOptions
        );
        renderStore.paymentElement.mount(`#${stripePaymentElementId.value}`);

        renderStore.paymentElement.on("ready", () => {
            stripeReady.value = true;
        });

        renderStore.paymentElement.on("change", (event) => {
            //remove any existing error from store model state if exist
            if (renderStore.modelState.paymentMethodId) {
                delete renderStore.modelState.paymentMethodId;
            }

            if (renderStore.modelState.paymentMethodType) {
                delete renderStore.modelState.paymentMethodType;
            }

            //map stripe payment method to renderStore
            renderStore.gift.paymentMethodType = getPaymentMethodType(
                event.value?.type
            );

            //we do not want to tokenize until submit button is clicked if using wallet
            if (event.value?.type === PaymentMethod.ApplePay || event.value?.type === PaymentMethod.GooglePay) {
                renderStore.usingWallet = true;
            } else {
                renderStore.usingWallet = false;
            }

            if (event.complete) {
                renderStore.setPayPalApproval(false, null); //turn off paypal if user completes stripe payment
                renderStore.tokenizePaymentMethod();
            }
        });
    }
};

const loadStripeScript = () => {
    if (typeof window === "undefined" || !window.Stripe) {
        //add stripe script
        const script = document.createElement("script");
        script.id = "stripe-js";
        script.src = "https://js.stripe.com/v3/";
        script.async = true;
        script.defer = true;
        script.onload = () => {
            if (props.tokenizationKey && props.merchantAccount) {
                // @ts-ignore
                renderStore.stripe = window.Stripe(props.tokenizationKey, {
                    stripeAccount: props.merchantAccount,
                });

                if (renderStore.stripe) {
                    stripeLoaded.value = true;
                }
            }
        };
        document.head.appendChild(script);
    } else {
        if (props.tokenizationKey && props.merchantAccount) {
            // @ts-ignore
            renderStore.stripe = window.Stripe(props.tokenizationKey, {
                stripeAccount: props.merchantAccount,
            });

            if (renderStore.stripe) {
                stripeLoaded.value = true;
            }
        }
    }
};

const updatePaymentElementLayout = () => {
    if (renderStore.paymentElement) {
        renderStore.paymentElement.update({
            layout: props.component.customData.layout,
        });
    }
};

const updatePaymentElementBillingDetails = () => {
    if (renderStore.paymentElement) {
        const billingDetails = renderStore.buildStripeBillingDetails();
        renderStore.paymentElement.update({
            defaultValues: {
                billingDetails: {
                    name: billingDetails.name ?? '',
                    email: billingDetails.email ?? '',
                    address: {
                        postal_code: billingDetails.address?.postal_code ?? ''
                    }
                }
            }
        });
    }
};

const updatePaymentElementWallets = () => {
    if (renderStore.paymentElement) {
        renderStore.paymentElement.update({
            wallets: {
                applePay: props.component.customData.enableWallets
                    ? "auto"
                    : "never",
                googlePay: props.component.customData.enableWallets
                    ? "auto"
                    : "never",
            },
        });
    }
};

const updateStripeElementsAppearance = () => {
    if (renderStore.stripeElements) {
        renderStore.stripeElements.update({
            appearance: appearance.value,
        });
    }
};

const updateStripeElementsLocale = () => {
    if (renderStore.stripeElements) {
        renderStore.stripeElements.update({
            // @ts-ignore
            locale: props.component.customData.locale ?? "auto",
        });
    }
};

const updateStripeElementsPaymentMethods = () => {
    if (renderStore.stripeElements) {
        renderStore.stripeElements.update({
            paymentMethodTypes: props.component.customData.paymentMethods,
        });
    }
};

const updateStripeElementAmount = () => {
    if (renderStore.stripeElements) {
        if (renderStore.totalAmountWithCosts > 0) {
            renderStore.stripeElements.update({
                mode: renderStore.totalAmountWithCosts > 0 ? "payment" : "setup",
                amount: convertToStripeAmount(renderStore.totalAmountWithCosts),
            });
        } else {
            renderStore.stripeElements.update({
                mode: "setup",
            });
        }
    }
};

const convertToStripeAmount = (amount: number) => {
    const stripeAmount = amount * 100;

    //remove any trailing decimals, tripe will not accept decimal amounts as they want it in cents
    return Math.round(stripeAmount);
};

// lifecycle hooks
onMounted(async () => {
    if (props.formIsOnParentStep || props.readOnly) {
        loadStripeScript();
    }
});

onBeforeUnmount(() => {
    if (renderStore.builderMode) return;

    if (renderStore.paymentElement) {
        destroyElement(renderStore.paymentElement);
    }

    //remove stripe script
    const stripeScript = document.getElementById("stripe-js");
    if (stripeScript) {
        stripeScript.remove();
    }

    //find stripe script via src
    const stripeScriptSrc = document.querySelector(
        'script[src="https://js.stripe.com/v3/"]'
    );
    if (stripeScriptSrc) {
        stripeScriptSrc.remove();
    }

    renderStore.stripe = null;
    renderStore.stripeElements = null;
    renderStore.paymentElement = null;
    stripeLoaded.value = false;
});

watch(stripeLoaded, async (newVal) => {
    if (newVal) {
        loadStripeElements();
    }
});

watch(
    () => props.pageDesign,
    () => {
        if (stripeLoaded.value) {
            updateStripeElementsAppearance();
        }
    },
    { deep: true }
);

watch(
    () => props.component.customData,
    () => {
        if (stripeLoaded.value) {
            updateStripeElementsPaymentMethods();
            updatePaymentElementLayout();
            updateStripeElementsAppearance();
            updatePaymentElementWallets();
            updateStripeElementsLocale();
        }
    },
    { deep: true }
);

watch(
    () => renderStore.donor.billingAddress,
    () => {
        updatePaymentElementBillingDetails();
    },
    { deep: true }
);

watch(
    () => renderStore.totalAmount,
    () => {
        if (stripeLoaded.value) {
            updateStripeElementAmount();
        }
    },
    { deep: true, immediate: true }
);

watch(
    () => renderStore.submissionRequiresAction,
    (val) => {
        //if action is required, we need to confirm card payment through stripe
        if (val && renderStore.paymentClientSecret) {
            renderStore.stripe
                ?.confirmCardPayment(renderStore.paymentClientSecret)
                .then((result) => {
                    if (result.error) {
                        renderStore.handleFailure(result.error.message);
                    } else {
                        renderStore.paymentId = result.paymentIntent.id;
                        renderStore.submitDonation();
                    }
                });
        }
    }
);

watch(
    () => renderStore.gift.startDate,
    async () => {
        loadStripeElements();
    }
);

watch(
    () => props.formIsOnParentStep,
    async (newVal) => {
        if (newVal && !stripeLoaded.value) {
            loadStripeScript();
        }
    }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!stripeReady.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("section", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  (
                                __props.component.customData.layout.type === 'tabs'
                            )
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, _cache[0] || (_cache[0] = [
                        _createStaticVNode("<section class=\"rd-flex rd-items-center rd-w-full rd-gap-x-3\"><span class=\"tab rd-w-3/6\"><div class=\"title\"></div></span><span class=\"tab rd-w-3/6\"><div class=\"title\"></div></span></section><div class=\"input-element loading-row rd-flex rd-w-full sbp:rd-hidden\"><span class=\"content\"></span></div><section class=\"rd-flex rd-items-center rd-w-full loading-row rd-gap-x-3\"><span class=\"input-element rd-w-full rd-hidden sbp:rd-flex sbp:rd-w-1/2\"><span class=\"content\"></span></span><div class=\"rd-w-full sbp:rd-w-1/2 rd-flex rd-gap-x-3\"><span class=\"input-element rd-flex rd-w-3/6\"><span class=\"content\"></span></span><span class=\"input-element rd-flex rd-w-3/6\"><span class=\"content\"></span></span></div></section><section class=\"rd-flex rd-items-center rd-gap-x-3 rd-w-full loading-row\"><span class=\"input-element rd-flex rd-w-3/6\"><span class=\"content\"></span></span><span class=\"input-element rd-flex rd-w-3/6\"><span class=\"content\"></span></span></section><span class=\"rd-sr-only\"> Loading Payment Fields... </span>", 5)
                      ])))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _cache[1] || (_cache[1] = _createStaticVNode("<div class=\"card-loading rd-w-full\"><div class=\"title\"></div><div class=\"input-element loading-row rd-flex rd-w-full sbp:rd-hidden\"><span class=\"content\"></span></div><section class=\"rd-flex rd-items-center rd-w-full loading-row rd-gap-x-3\"><span class=\"input-element rd-w-full rd-hidden sbp:rd-flex sbp:rd-w-1/2\"><span class=\"content\"></span></span><div class=\"rd-w-full sbp:rd-w-1/2 rd-flex rd-gap-x-3\"><span class=\"input-element rd-flex rd-w-3/6\"><span class=\"content\"></span></span><span class=\"input-element rd-flex rd-w-3/6\"><span class=\"content\"></span></span></div></section><section class=\"rd-flex rd-items-center rd-w-full loading-row rd-gap-x-3\"><span class=\"input-element rd-flex rd-w-3/6\"><span class=\"content\"></span></span><span class=\"input-element rd-flex rd-w-3/6\"><span class=\"content\"></span></span></section></div><div class=\"bank-loading rd-w-full\"><div class=\"title\"></div></div><span class=\"rd-sr-only\"> Loading Payment Fields... </span>", 3))
                      ], 64))
                ])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_7, _toDisplayString(_unref(renderStore).paymentElementError), 1),
    _createElementVNode("div", { id: stripePaymentElementId.value }, null, 8, _hoisted_8)
  ], 64))
}
}

})